import { AxiosResponse } from "axios";
import { StudentDTO } from "../../models/StudentDTO";
import { axiosInstance } from "./axiosInstance";
import { PaginatedResponse } from "../../models/common/PaginatedResponseDTO";
import { StudentAuditEntryDTO, StudentDocumentDTO, StudentDocumentsResponseDTO } from "../../models/EnrollmentHistoryDTO";

const RESOURCE_URL_V2 = "/admin/campus/";
const RESOURCE_URL_ADMIN_V2 = "/admin/";

export class StudentService {

    async getStudentById(campusId: number, studentId: number): Promise<AxiosResponse<StudentDTO>> {
        const response = await axiosInstance
            .get<StudentDTO>(`${RESOURCE_URL_V2}${campusId}/students/${studentId}/_details`);
        return response;
    }

    async addToCampuses(campusId: number, studentId: number): Promise<AxiosResponse<boolean>> {
        let payload: any = {
            campusId: campusId,
            studentId: studentId
        }
        const response = await axiosInstance
            .post<boolean>(`${RESOURCE_URL_V2}${campusId}/students/_addToCampuses`, payload);
        return response;
    }

    /** Agrega al alumno de ESTE plantel (currentCampusId) a otro; el permiso se revisa aquí, no en el destino (2026-09-24). */
    async addToOtherCampus(currentCampusId: number, targetCampusId: number, studentId: number): Promise<AxiosResponse<any>> {
        return axiosInstance.post(`${RESOURCE_URL_V2}${currentCampusId}/studentCampuses/_add`, { studentId, targetCampusId });
    }

    async getCampusesByStudent(studentId: number): Promise<AxiosResponse> {
        let payload: any = {
            params: {
                studentId: studentId
            }
        }
        const response = await axiosInstance
            .get(`${RESOURCE_URL_ADMIN_V2}students/${studentId}/campuses`, payload);
        return response;
    }

    getStudentsByCampusId = async (campusId: number, perPage: number, criteria?: any): Promise<AxiosResponse<PaginatedResponse<StudentDTO>>> => {
        let uri = `${RESOURCE_URL_V2}${campusId}/students/_list?perPage=${perPage}`;
        if (criteria && typeof criteria === 'object' && Object.keys(criteria).length > 0) {
            try {
                const criteriaJson = JSON.stringify(criteria);
                uri = `${uri}&criteria=${encodeURIComponent(criteriaJson)}`;
            } catch (error) {
                console.error("Error serializando criteria as JSON:", error);
                throw new Error("Invalid criteria format");
            }
        }
        const response = await axiosInstance.get<PaginatedResponse<StudentDTO>>(uri);
        return response;
    }

    getPaginatedStudents = async (uri: string): Promise<AxiosResponse<PaginatedResponse<StudentDTO>>> => {
        let response = await axiosInstance.get<PaginatedResponse<StudentDTO>>(uri);
        return response;
    }

    deleteStudent = async (campusId: number, studentId: number): Promise<AxiosResponse> => {
        const url = `${RESOURCE_URL_V2}${campusId}/students/${studentId}`;
        let response = await axiosInstance.delete(url);
        return response;
    }

    /* Student documents (ID copies, guardian ID, contracts) — stored on the server's private disk. */

    //Observaciones en bitácora: se listan, se agregan y solo el autor corrige la suya
    //dentro de los primeros minutos (Esteban, 2026-09-11).
    getStudentNotes = async (campusId: number, studentId: number): Promise<AxiosResponse<any>> => {
        return axiosInstance.get<any>(`${RESOURCE_URL_V2}${campusId}/students/${studentId}/_notes`);
    }

    addStudentNote = async (campusId: number, studentId: number, note: string): Promise<AxiosResponse<any>> => {
        //Ruta con permiso propio (studentNotes): Admisiones escribe observaciones sin editar la ficha (2026-09-24).
        return axiosInstance.post<any>(`${RESOURCE_URL_V2}${campusId}/studentNotes/${studentId}/_add`, {note});
    }

    updateStudentNote = async (campusId: number, studentId: number, noteId: number, note: string): Promise<AxiosResponse<any>> => {
        return axiosInstance.put<any>(`${RESOURCE_URL_V2}${campusId}/studentNotes/${studentId}/_edit/${noteId}`, {note});
    }

    getStudentDocuments = async (campusId: number, studentId: number): Promise<AxiosResponse<StudentDocumentsResponseDTO>> => {
        return axiosInstance.get<StudentDocumentsResponseDTO>(`${RESOURCE_URL_V2}${campusId}/students/${studentId}/_documents`);
    }

    uploadStudentDocument = async (campusId: number, studentId: number, typeId: number, description: string, file: File): Promise<AxiosResponse<StudentDocumentDTO>> => {
        const form = new FormData();
        form.append("typeId", String(typeId));
        if (description.trim() !== "") {
            form.append("description", description.trim());
        }
        form.append("file", file);
        return axiosInstance.post<StudentDocumentDTO>(`${RESOURCE_URL_V2}${campusId}/students/${studentId}/_documents`, form, {
            headers: { "Content-Type": "multipart/form-data" }
        });
    }

    /** Returns the file as a Blob so the UI can open it in a new tab (the endpoint needs the bearer token). */
    downloadStudentDocument = async (campusId: number, studentId: number, documentId: number): Promise<AxiosResponse<Blob>> => {
        return axiosInstance.get<Blob>(`${RESOURCE_URL_V2}${campusId}/students/${studentId}/_documents/${documentId}`, { responseType: "blob" });
    }

    deleteStudentDocument = async (campusId: number, studentId: number, documentId: number): Promise<AxiosResponse> => {
        return axiosInstance.delete(`${RESOURCE_URL_V2}${campusId}/students/${studentId}/_documents/${documentId}`);
    }

    /** Append-only activity log of the student (profile edits, enrollments, documents), newest first. */
    getStudentAuditLog = async (campusId: number, studentId: number): Promise<AxiosResponse<StudentAuditEntryDTO[]>> => {
        return axiosInstance.get<StudentAuditEntryDTO[]>(`${RESOURCE_URL_V2}${campusId}/students/${studentId}/_auditLog`);
    }
}